import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { PageHero } from '../components/PageHero';
import { CtaBand } from '../components/CtaBand';
import { Reveal } from '../components/Reveal';
import { usePageTitle } from '../hooks/usePageTitle';
import { ASSESSMENT_URL, HOW_WE_HELP } from '../config/foundation';

interface Service {
  title: string;
  desc: string;
  cta: string;
  to?: string;
  href?: string;
  flagship?: boolean;
}

const SERVICES: Service[] = [
  { flagship: true, title: 'LifeSpark360&#8482;', desc: 'Our flagship 12-week coaching programme built on the SPARK Method — from self-discovery to sustained momentum, with 1-to-1 support throughout.', to: '/programmes', cta: 'Explore the programme' },
  { title: 'Seedbed', desc: 'An 8-week foundation programme for those starting out — building clarity and confidence before the full LifeSpark360 journey.', to: '/programmes', cta: 'Learn more' },
  { title: 'Readiness Assessment', desc: 'A free 41-question assessment across six dimensions, giving each person a personalised profile and a recommended next step.', href: ASSESSMENT_URL, cta: 'Take the assessment' },
  { title: 'CV &amp; skills tools', desc: 'A free AI-powered CV check and a full CV Assessor that scores against a 7-Point Framework, with specific, practical improvements.', to: '/cv-lite', cta: 'Try the free CV check' },
  { title: 'Mentoring &amp; community', desc: 'Participants are supported by coaches and mentors, and join a community where those who have moved forward help others do the same.', to: '/get-involved', cta: 'Become a mentor' },
  { title: 'Grants, advice &amp; advocacy', desc: 'Where appropriate, we provide grants and finance, signposting, and practical advice and advocacy to remove the barriers people face.', to: '/apply', cta: 'Apply for support' },
];

export function WhatWeDo() {
  usePageTitle('What We Do');
  return (
    <Layout>
      <PageHero
        eyebrow="Our Work"
        title={<>What we{' '}<span className="text-mustard-gold">do</span></>}
      >
        We combine structured programmes, practical tools, advice, and funding to help people
        build skills, confidence, and a sustainable livelihood.
      </PageHero>

      {/* Programmes & services */}
      <section className="py-16 sm:py-24">
        <div className="section">
          <Reveal className="text-center mb-12 sm:mb-16">
            <p className="eyebrow mb-3">Programmes &amp; Services</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-display">
              <span className="text-gradient">How we help people move forward</span>
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.map((s, i) => (
              <Reveal key={s.title} delay={(i % 3) * 120}>
                <ServiceCard {...s} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* How we help */}
      <section className="bg-white py-16 sm:py-24 border-y border-slate-100">
        <div className="section">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
            <Reveal>
              <p className="eyebrow mb-3">Our Methods</p>
              <h2 className="font-display text-2xl sm:text-3xl font-bold text-mustard-green-dark tracking-display mb-5">
                The ways we make a difference
              </h2>
              <p className="text-slate-500 leading-relaxed">
                As a charity, our support takes many forms. We meet each person and community where
                they are, and use whichever combination of services will create the most lasting
                impact.
              </p>
            </Reveal>
            <Reveal delay={120}>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {HOW_WE_HELP.map((item) => (
                  <li key={item} className="flex items-start gap-3 p-4 rounded-xl border border-slate-100 bg-mustard-cream/40 hover:border-mustard-green-mid/40 transition-colors">
                    <span className="w-5 h-5 rounded-full bg-mustard-green-dark/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <svg className="w-3 h-3 text-mustard-green-dark" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                    </span>
                    <span className="text-sm text-mustard-charcoal font-medium">{item}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Who & where */}
      <section className="py-16 sm:py-24">
        <div className="section">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Reveal>
              <div className="card h-full p-8">
                <h3 className="font-display text-lg font-bold text-mustard-green-dark mb-3">Who we help</h3>
                <p className="text-sm text-slate-500 leading-relaxed">
                  We work with members of the general public who want to build skills and independence,
                  including older people, people from diverse ethnic and cultural backgrounds, and other
                  charities and voluntary bodies we support to extend our reach.
                </p>
              </div>
            </Reveal>
            <Reveal delay={120}>
              <div className="card h-full p-8">
                <h3 className="font-display text-lg font-bold text-mustard-green-dark mb-3">Where we work</h3>
                <p className="text-sm text-slate-500 leading-relaxed">
                  We operate throughout England and Wales, and in Scotland &mdash; delivering programmes
                  online so that people can take part wherever they are, alongside in-person support
                  where it is available.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <CtaBand
        title="Not sure where to start?"
        subtitle="Take the free readiness assessment, or apply directly for a place on one of our programmes."
      >
        <a href={ASSESSMENT_URL} className="btn-gold text-center">Take the Assessment</a>
        <Link to="/apply" className="glass px-6 py-3 text-white rounded-xl font-semibold text-sm hover:bg-white/20 transition-all text-center">
          Apply for Support
        </Link>
      </CtaBand>
    </Layout>
  );
}

function ServiceCard({ title, desc, to, href, cta, flagship }: Service) {
  return (
    <div className={`group card h-full p-6 sm:p-8 flex flex-col relative overflow-hidden ${flagship ? 'ring-2 ring-mustard-gold' : ''}`}>
      {flagship && <div className="absolute top-0 left-0 right-0 h-1 bg-gold-gradient" aria-hidden="true" />}
      {flagship && (
        <span className="badge bg-mustard-gold/15 text-mustard-gold self-start mb-3">Flagship</span>
      )}
      <h3 className="font-display text-lg font-bold text-mustard-green-dark mb-2" dangerouslySetInnerHTML={{ __html: title }} />
      <p className="text-sm text-slate-500 leading-relaxed mb-5 flex-1" dangerouslySetInnerHTML={{ __html: desc }} />
      {href ? (
        <a href={href} className="inline-flex items-center gap-1.5 text-mustard-green font-semibold text-sm">
          {cta} <span className="transition-transform group-hover:translate-x-1">&rarr;</span>
        </a>
      ) : (
        <Link to={to!} className="inline-flex items-center gap-1.5 text-mustard-green font-semibold text-sm">
          {cta} <span className="transition-transform group-hover:translate-x-1">&rarr;</span>
        </Link>
      )}
    </div>
  );
}
